/**
 * «Vises på»: which screens a banner, an announcement or an event is shown on.  OA-033, 2026-10-01.
 *
 * All screens is the default and stays the default until somebody chooses
 * otherwise. Groups and single screens can be mixed. Narrowing a janazah needs
 * somebody who may approve notices — the API says so in words if not.
 * Shown only to those who manage screens (the API answers 403 otherwise, and
 * then this renders nothing).
 */
import React, { useState } from 'react';
import { call, errText, send, type Body } from '../lib/api';
import { Btn } from './ui';

type Target = { type: 'group' | 'screen'; id: string };

export function TargetPicker({ kind, id }: { kind: 'banner' | 'notice' | 'event'; id: string }) {
  const [open, setOpen] = useState(false);
  const [fleet, setFleet] = useState<Body | null>(null);
  const [targets, setTargets] = useState<Target[] | null>(null);
  const [summary, setSummary] = useState('');
  const [msg, setMsg] = useState('');
  const [hidden, setHidden] = useState(false);
  if (hidden) return null;

  const load = async () => {
    const [f, t] = await Promise.all([call('/screen-groups').catch(() => null), call(`/targets/${kind}/${encodeURIComponent(id)}`).catch(() => null)]);
    if (!f || !t || f.code === 'not_permitted' || !f.items) { setHidden(true); return; }
    setFleet(f); setTargets(t.targets || []); setSummary(t.describe || 'Alle skjermer');
  };
  const has = (type: Target['type'], tid: string) => Boolean(targets && targets.some((x) => x.type === type && x.id === tid));
  const flip = (type: Target['type'], tid: string) => setTargets((cur) => {
    const list = cur || [];
    return has(type, tid) ? list.filter((x) => !(x.type === type && x.id === tid)) : [...list, { type, id: tid }];
  });
  const save = async (list: Target[]) => {
    const r = await send(`/targets/${kind}/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ targets: list.length ? list : [{ type: 'all' }] }) });
    if (!r.ok) { setMsg(errText(r.body)); return; }
    setTargets(r.body.targets); setSummary(r.body.describe); setMsg('Lagret. Skjermene får det ved neste oppdatering.');
  };

  return (
    <details className="tp" onToggle={(e) => { const o = (e.target as HTMLDetailsElement).open; setOpen(o); if (o && !fleet) load(); }}>
      <summary>Vises på{summary ? `: ${summary}` : ''}</summary>
      {open && fleet && targets && (
        <div className="targets">
          <label className="check"><input type="checkbox" checked={!targets.length} onChange={() => setTargets([])} /> Alle skjermer</label>
          {(fleet.items || []).map((g: Body) => (
            <div key={g.id}>
              <label className="check"><input type="checkbox" checked={has('group', g.id)} onChange={() => flip('group', g.id)} /> Gruppe: <b>{g.name}</b> ({g.screens.length})</label>
              {(g.screens || []).map((s: Body) => (
                <label key={s.id} className="check" style={{ marginLeft: 24 }}><input type="checkbox" checked={has('screen', s.id)} onChange={() => flip('screen', s.id)} /> {s.name}</label>))}
            </div>))}
          {(fleet.ungrouped || []).map((s: Body) => (
            <label key={s.id} className="check"><input type="checkbox" checked={has('screen', s.id)} onChange={() => flip('screen', s.id)} /> {s.name}</label>))}
          <Btn onClick={() => save(targets)}>Lagre</Btn> <span role="status" className="note">{msg}</span>
        </div>
      )}
    </details>
  );
}
